// Forwardsflow Role Configuration
// Defines all roles, permissions, and access control

export const ROLES = {
  FORWARDSFLOW_ADMIN: 'forwardsflow_admin',
  PLATFORM_ADMIN: 'platform_admin', // Alias for database compatibility
  BANK_ADMIN: 'bank_admin',
  BANK_COMPLIANCE_MAKER: 'bank_compliance_maker',
  BANK_COMPLIANCE_CHECKER: 'bank_compliance_checker',
  BANK_TREASURY_DEALER: 'bank_treasury_dealer',
  BANK_FORWARDS_DEALER: 'bank_forwards_dealer',
  INVESTOR_FAMILY_OFFICE: 'investor_family_office',
  INVESTOR_INDIVIDUAL: 'investor_individual',
};

export const TENANT_TYPES = {
  PLATFORM: 'platform',
  BANK: 'bank',
  INVESTOR: 'investor',
};

// Platform admin config (shared between forwardsflow_admin and platform_admin)
const PLATFORM_ADMIN_CONFIG = {
  label: 'Forwardsflow Admin',
  tenantType: TENANT_TYPES.PLATFORM,
  level: 0,
  color: 'red',
  icon: 'Shield',
  description: 'System super admin - manages all tenants',
  permissions: {
    viewAllTenants: true,
    createTenants: true,
    editTenants: true,
    suspendTenants: true,
    deleteTenants: true,
    viewTenantAdmins: true,
    createTenantAdmins: true,
    editTenantAdmins: true,
    suspendTenantAdmins: true,
    viewAllTransactions: true,
    viewComplianceData: true,
    viewAMLData: true,
    viewKYCData: true,
    viewPlatformPnL: true,
    viewPlatformRevenue: true,
    viewTenantUsage: true,
    viewSystemHealth: true,
    manageSystemSettings: true,
    manageBilling: true,
    manageIntegrations: true,
  },
  dashboardPath: '/admin',
  defaultRoute: '/admin',
};

export const ROLE_CONFIG = {
  [ROLES.FORWARDSFLOW_ADMIN]: PLATFORM_ADMIN_CONFIG,
  [ROLES.PLATFORM_ADMIN]: PLATFORM_ADMIN_CONFIG, // Database uses this role name

  [ROLES.BANK_ADMIN]: {
    label: 'Bank Admin',
    tenantType: TENANT_TYPES.BANK,
    level: 1,
    color: 'green',
    icon: 'Building2',
    description: 'Bank administrator - full access within own bank',
    canManage: ['bank_compliance_maker', 'bank_compliance_checker', 'bank_treasury_dealer', 'bank_forwards_dealer'],
    mutuallyExclusive: ['bank_compliance_maker', 'bank_compliance_checker'],
    permissions: {
      viewOwnStaff: true,
      createStaff: true,
      editStaff: true,
      suspendStaff: true,
      deleteStaff: true,
      viewAllBankData: true,
      viewTransactions: true,
      viewComplianceData: true,
      viewAllAnalytics: true,
      manageBankSettings: true,
      manageIntegrations: true,
      manageBankProfile: true,
      viewAuditTrail: true,
    },
    dashboardPath: '/bank/admin',
    defaultRoute: '/bank/admin',
    dashboardTabs: ['overview', 'users_and_roles', 'interfaces_parameters', 'bank_profile', 'audit'],
  },

  [ROLES.BANK_COMPLIANCE_MAKER]: {
    label: 'Compliance Maker',
    tenantType: TENANT_TYPES.BANK,
    level: 2,
    color: 'orange',
    icon: 'Shield',
    description: 'Compliance first-leg reviewer - KYC review and adjudication',
    makerCheckerPair: 'bank_compliance_checker',
    mutuallyExclusive: ['bank_compliance_checker'],
    permissions: {
      'kyc:review': true,
      'kyc:adjudicate': true,
      'cds:sign_second': true,
      'screening:adjudicate': true,
      'documents:manage': true,
      viewComplianceData: true,
      viewAMLData: true,
      viewKYCData: true,
      flagSuspiciousActivity: true,
      viewAllTransactions: true,
      viewAuditTrail: true,
    },
    dashboardPath: '/bank/compliance-maker',
    defaultRoute: '/bank/compliance-maker',
    dashboardTabs: ['kyc_queue', 'cds_signing', 'screening', 'documents', 'audit'],
  },

  [ROLES.BANK_COMPLIANCE_CHECKER]: {
    label: 'Compliance Checker',
    tenantType: TENANT_TYPES.BANK,
    level: 2,
    color: 'amber',
    icon: 'ClipboardCheck',
    description: 'Compliance second-leg confirmer - KYC confirmation and periodic reviews',
    makerCheckerPair: 'bank_compliance_maker',
    mutuallyExclusive: ['bank_compliance_maker'],
    permissions: {
      'kyc:confirm': true,
      'kyc:return': true,
      'cds:sign_first': true,
      'screening:read': true,
      'screening:escalate': true,
      'documents:read': true,
      'periodic_reviews:manage': true,
      viewComplianceData: true,
      viewKYCData: true,
      viewAllTransactions: true,
      viewAuditTrail: true,
    },
    dashboardPath: '/bank/compliance-checker',
    defaultRoute: '/bank/compliance-checker',
    dashboardTabs: ['kyc_confirmation', 'cds_signing', 'screening', 'documents', 'periodic_reviews', 'audit'],
  },

  [ROLES.BANK_TREASURY_DEALER]: {
    label: 'Treasury Dealer',
    tenantType: TENANT_TYPES.BANK,
    level: 2,
    color: 'blue',
    icon: 'Wallet',
    description: 'Foreign exchange spot trading and treasury operations',
    permissions: {
      viewFXQuotes: true,
      submitFXQuote: true,
      declineFXQuote: true,
      amendQuote: true,
      withdrawQuote: true,
      viewSettlement: true,
      confirmSettlement: true,
      viewBlotter: true,
      viewPositionLimits: true,
      configurePositionLimits: true,
      viewFXAnalytics: true,
      viewAuditTrail: true,
      viewAutoRules: true,
      viewCompetitiveContext: true,
      viewCounterpartyDetail: true,
      viewEvidencePack: true,
      requestLimitOverride: true,
      exportRegulatory: true,
      viewComplianceAudit: true,
    },
    dashboardPath: '/bank/treasury',
    defaultRoute: '/bank/treasury',
    dashboardTabs: ['fx_quotes', 'settlement', 'blotter', 'position_limits', 'analytics', 'auto_rules', 'compliance_audit'],
  },

  [ROLES.BANK_FORWARDS_DEALER]: {
    label: 'Forwards Dealer',
    tenantType: TENANT_TYPES.BANK,
    level: 2,
    color: 'indigo',
    icon: 'TrendingUp',
    description: 'Foreign exchange forwards trading and contracts management',
    permissions: {
      viewForwardsQuotes: true,
      submitForwardsQuote: true,
      declineForwardsQuote: true,
      viewSettlement: true,
      confirmSettlement: true,
      viewContractsBook: true,
      viewPositionLimits: true,
      configurePositionLimits: true,
      viewForwardsAnalytics: true,
      viewAuditTrail: true,
    },
    dashboardPath: '/bank/forwards',
    defaultRoute: '/bank/forwards',
    dashboardTabs: ['forwards_quotes', 'settlement_acceptance', 'contracts_book', 'position_limits', 'analytics', 'audit'],
  },

  [ROLES.INVESTOR_FAMILY_OFFICE]: {
    label: 'Family Office',
    tenantType: TENANT_TYPES.INVESTOR,
    level: 1,
    color: 'indigo',
    icon: 'Briefcase',
    description: 'Family office administrator - full access within own organization',
    permissions: {
      viewOwnStaff: true,
      createStaff: true,
      editStaff: true,
      suspendStaff: true,
      deleteStaff: true,
      viewAllInvestments: true,
      createInvestments: true,
      approveInvestments: true,
      viewOpportunities: true,
      submitBids: true,
      viewPortfolio: true,
      managePortfolio: true,
      viewComplianceData: true,
      submitKYCDocuments: true,
      viewTransactionHistory: true,
      viewAllAnalytics: true,
      viewSecondaryMarket: true,
      manageSecondaryListings: true,
      manageOrgSettings: true,
      manageApprovalThresholds: true,
      viewProjections: true,
    },
    dashboardPath: '/investor/admin',
    defaultRoute: '/investor/admin',
    dashboardTabs: ['administration', 'yield', 'rates', 'compliance', 'hedging', 'execution', 'settlement', 'portfolio', 'liquidity', 'projections', 'profile', 'messages'],
  },

  [ROLES.INVESTOR_INDIVIDUAL]: {
    label: 'Individual Investor',
    tenantType: TENANT_TYPES.INVESTOR,
    level: 2,
    color: 'cyan',
    icon: 'LineChart',
    description: 'Individual investor - personal investment management',
    permissions: {
      viewOpportunities: true,
      viewAllInvestments: true,
      submitBids: true,
      viewPortfolio: true,
      viewComplianceData: true,
      submitKYCDocuments: true,
      viewTransactionHistory: true,
      viewAnalytics: true,
      viewSecondaryMarket: true,
      manageSecondaryListings: true,
      managePersonalSettings: true,
      viewProjections: true,
    },
    dashboardPath: '/investor',
    defaultRoute: '/investor',
    dashboardTabs: ['settings', 'yield', 'rates', 'compliance', 'hedging', 'execution', 'settlement', 'portfolio', 'liquidity', 'projections', 'profile', 'messages'],
  },
};

// ============================================================
// DEMO USERS - ALL 8 CREDENTIALS VERIFIED
// ============================================================
export const DEMO_USERS = {
  // 1. Super Admin - Password: Admin123!
  'admin@forwardsflow.com': {
    id: 'ff-admin-001',
    email: 'admin@forwardsflow.com',
    password: 'Admin123!',
    name: 'System Administrator',
    role: ROLES.FORWARDSFLOW_ADMIN,
    tenantId: 'forwardsflow-platform',
    tenantName: 'Forwardsflow',
    tenantType: TENANT_TYPES.PLATFORM,
    orgId: 'forwardsflow-platform',
    avatar: null,
    createdAt: '2024-01-01T00:00:00Z',
  },

  // 2. Bank Admin - Password: Demo123!
  'admin@equityafrica.com': {
    id: 'ea-admin-001',
    email: 'admin@equityafrica.com',
    password: 'Demo123!',
    name: 'Amoroso Gombe',
    role: ROLES.BANK_ADMIN,
    tenantId: 'bank-equity-africa',
    tenantName: 'Equity Africa Bank',
    tenantType: TENANT_TYPES.BANK,
    orgId: 'bank-equity-africa',
    jobTitle: 'Treasury Director',
    avatar: null,
    createdAt: '2024-02-01T00:00:00Z',
  },

  // 3. Compliance Maker - Password: Demo123!
  'compliance@equityafrica.com': {
    id: 'ea-compliance-maker-001',
    email: 'compliance@equityafrica.com',
    password: 'Demo123!',
    name: 'Sarah Kimani',
    role: ROLES.BANK_COMPLIANCE_MAKER,
    tenantId: 'bank-equity-africa',
    tenantName: 'Equity Africa Bank',
    tenantType: TENANT_TYPES.BANK,
    orgId: 'bank-equity-africa',
    jobTitle: 'Compliance Officer',
    avatar: null,
    createdAt: '2024-03-20T00:00:00Z',
  },

  // 4. Compliance Checker - Password: Demo123!
  'checker@equityafrica.com': {
    id: 'ea-compliance-checker-001',
    email: 'checker@equityafrica.com',
    password: 'Demo123!',
    name: 'Faith Wanjiku',
    role: ROLES.BANK_COMPLIANCE_CHECKER,
    tenantId: 'bank-equity-africa',
    tenantName: 'Equity Africa Bank',
    tenantType: TENANT_TYPES.BANK,
    orgId: 'bank-equity-africa',
    jobTitle: 'Compliance Checker',
    avatar: null,
    createdAt: '2024-03-25T00:00:00Z',
  },

  // 5. Treasury Dealer - Password: Demo123!
  'fx@equityafrica.com': {
    id: 'ea-treasury-001',
    email: 'fx@equityafrica.com',
    password: 'Demo123!',
    name: 'Grace Mwangi',
    role: ROLES.BANK_TREASURY_DEALER,
    tenantId: 'bank-equity-africa',
    tenantName: 'Equity Africa Bank',
    tenantType: TENANT_TYPES.BANK,
    orgId: 'bank-equity-africa',
    jobTitle: 'Foreign Exchange Spot Dealer',
    avatar: null,
    createdAt: '2024-04-01T00:00:00Z',
  },

  // 6. Forwards Dealer - Password: Demo123!
  'forwards@equityafrica.com': {
    id: 'ea-forwards-001',
    email: 'forwards@equityafrica.com',
    password: 'Demo123!',
    name: 'David Kariuki',
    role: ROLES.BANK_FORWARDS_DEALER,
    tenantId: 'bank-equity-africa',
    tenantName: 'Equity Africa Bank',
    tenantType: TENANT_TYPES.BANK,
    orgId: 'bank-equity-africa',
    jobTitle: 'Foreign Exchange Forwards Dealer',
    avatar: null,
    createdAt: '2024-04-15T00:00:00Z',
  },

  // 7. Family Office - Password: Demo123!
  'admin@horizonimpact.com': {
    id: 'hi-admin-001',
    email: 'admin@horizonimpact.com',
    password: 'Demo123!',
    name: 'Victoria Chen',
    role: ROLES.INVESTOR_FAMILY_OFFICE,
    tenantId: 'inv-horizon-impact',
    tenantName: 'Horizon Impact Partners',
    tenantType: TENANT_TYPES.INVESTOR,
    orgId: 'inv-horizon-impact',
    jobTitle: 'Family Office Director',
    avatar: null,
    createdAt: '2024-02-15T00:00:00Z',
  },

  // 8. Individual Investor - Password: Demo123!
  'analyst@horizonimpact.com': {
    id: 'hi-analyst-001',
    email: 'analyst@horizonimpact.com',
    password: 'Demo123!',
    name: 'James Okonkwo',
    role: ROLES.INVESTOR_INDIVIDUAL,
    tenantId: 'inv-horizon-impact',
    tenantName: 'Horizon Impact Partners',
    tenantType: TENANT_TYPES.INVESTOR,
    orgId: 'inv-horizon-impact',
    jobTitle: 'Individual Investor',
    avatar: null,
    createdAt: '2024-05-01T00:00:00Z',
  },
};

// Helper Functions
export const getRoleConfig = (role) => ROLE_CONFIG[role] || null;

export const hasPermission = (userRole, permission) => {
  const config = ROLE_CONFIG[userRole];
  return config?.permissions?.[permission] === true;
};

export const getTenantType = (role) => {
  const config = ROLE_CONFIG[role];
  return config?.tenantType || null;
};

export const isPlatformRole = (role) => getTenantType(role) === TENANT_TYPES.PLATFORM;
export const isBankRole = (role) => getTenantType(role) === TENANT_TYPES.BANK;
export const isInvestorRole = (role) => getTenantType(role) === TENANT_TYPES.INVESTOR;

export const isAdminRole = (role) => {
  return [ROLES.FORWARDSFLOW_ADMIN, ROLES.PLATFORM_ADMIN, ROLES.BANK_ADMIN, ROLES.INVESTOR_FAMILY_OFFICE].includes(role);
};

export const getDashboardPath = (role) => {
  const config = ROLE_CONFIG[role];
  return config?.dashboardPath || '/login';
};

export const getRolesByTenantType = (tenantType) => {
  return Object.entries(ROLE_CONFIG)
    .filter(([_, config]) => config.tenantType === tenantType)
    .map(([role, config]) => ({ role, ...config }));
};

export const isMutuallyExclusive = (role1, role2) => {
  const config = ROLE_CONFIG[role1];
  return config?.mutuallyExclusive?.includes(role2) || false;
};

export default {
  ROLES,
  TENANT_TYPES,
  ROLE_CONFIG,
  DEMO_USERS,
  getRoleConfig,
  hasPermission,
  getTenantType,
  isPlatformRole,
  isBankRole,
  isInvestorRole,
  isAdminRole,
  getDashboardPath,
  getRolesByTenantType,
  isMutuallyExclusive,
};
